import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight, CalendarMinus, SlidersHorizontal, GitMerge, FileText, Megaphone,
  PartyPopper, Cake, Users as UsersIcon, CheckCircle2, Palmtree, Receipt,
} from "lucide-react";
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell, PieChart, Pie, Tooltip } from "recharts";
import api from "../lib/api";
import { Loading, Avatar } from "../components/ui";
import { NAV } from "../components/nav";
import { useAuth } from "../context/AuthContext";

const PIE = ["#2563EB", "#16A34A", "#D97706", "#DC2626", "#7C3AED"];

const REQUESTS = [
  { label: "Apply Leave", to: "/leave", icon: CalendarMinus, cls: "bg-rose-50 text-rose-600 border-rose-100 dark:bg-rose-500/10 dark:border-rose-500/20" },
  { label: "Update Preferences", to: "/me", icon: SlidersHorizontal, cls: "bg-sky-50 text-sky-600 border-sky-100 dark:bg-sky-500/10 dark:border-sky-500/20" },
  { label: "Initiate Flow", to: "/approvals", icon: GitMerge, cls: "bg-amber-50 text-amber-600 border-amber-100 dark:bg-amber-500/10 dark:border-amber-500/20" },
  { label: "Request Letter", to: "/documents", icon: FileText, cls: "bg-violet-50 text-violet-600 border-violet-100 dark:bg-violet-500/10 dark:border-violet-500/20" },
];

// apps shown in the dashboard "Quick Apps" grid (role-filtered)
const QUICK_PATHS = ["/employees", "/onboarding", "/performance", "/payroll", "/reports", "/travel", "/documents", "/recruitment", "/settings", "/learning", "/assets", "/flows"];

function Hero({ announcements }) {
  const [i, setI] = useState(0);
  const slides = announcements.length ? announcements : [{ title: "Welcome to SMIFS HRMS", body: "Your people, payroll and processes — all in one place." }];
  useEffect(() => {
    if (slides.length < 2) return;
    const t = setInterval(() => setI((x) => (x + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length]);
  const a = slides[i % slides.length];
  return (
    <div data-testid="dashboard-hero" className="relative h-48 overflow-hidden rounded-2xl border border-slate-200/60 bg-gradient-to-r from-sky-50 via-indigo-50 to-white shadow-sm dark:border-slate-800 dark:from-slate-800 dark:via-slate-800/60 dark:to-slate-900 md:h-56">
      {a.hasImage && a.id && (
        <>
          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/announcements/${a.id}/image`} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/75 to-white/20 dark:from-slate-900/95 dark:via-slate-900/75 dark:to-slate-900/20" />
        </>
      )}
      <div className="relative z-10 flex h-full flex-col justify-center p-6 md:max-w-[62%] md:p-8">
        <span className="mb-2 inline-flex w-fit items-center gap-1.5 rounded-full bg-accent/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-accent">Announcement</span>
        <h2 className="font-head text-xl font-extrabold leading-tight tracking-tight text-slate-900 dark:text-white md:text-2xl">{a.title}</h2>
        {a.body && <p className="mt-2 line-clamp-2 text-sm text-slate-600 dark:text-slate-300">{a.body}</p>}
      </div>
      <div className="pointer-events-none absolute right-6 top-1/2 hidden -translate-y-1/2 text-accent/15 md:block">
        <Megaphone size={150} strokeWidth={1.25} />
      </div>
      {slides.length > 1 && (
        <div className="absolute bottom-4 right-5 z-10 flex gap-1.5">
          {slides.map((_, x) => (
            <button key={x} onClick={() => setI(x)} aria-label={`Slide ${x + 1}`}
              className={`h-1.5 rounded-full transition-all ${x === i % slides.length ? "w-5 bg-accent" : "w-1.5 bg-slate-300 dark:bg-slate-600"}`} />
          ))}
        </div>
      )}
    </div>
  );
}

function TasksAwaiting({ tasks }) {
  return (
    <div className="card p-5" data-testid="tasks-awaiting">
      <div className="mb-4 flex items-center justify-between border-b border-slate-100 pb-4 dark:border-slate-800">
        <h3 className="font-head text-base font-bold text-slate-900 dark:text-white">Tasks Awaiting You ({tasks.length})</h3>
        <Link to="/approvals" data-testid="view-all-tasks" className="text-xs font-semibold text-accent hover:underline">View All</Link>
      </div>
      <div className="space-y-1">
        {tasks.length === 0 && <p className="py-6 text-center text-sm text-slate-400">You're all caught up — no pending approvals.</p>}
        {tasks.slice(0, 5).map((t) => (
          <Link key={t.id} to="/approvals" data-testid="task-item-row"
            className="group flex items-start gap-3 rounded-xl p-3 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50">
            <Avatar name={t.requesterName || t.entityType || "?"} size={36} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="truncate text-sm font-semibold text-slate-800 dark:text-slate-100">{t.flowName || `${(t.entityType || "Request")} Approval`}</p>
                <span className="rounded-full border border-amber-100 bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-600 dark:border-amber-500/20 dark:bg-amber-500/10">Stage {t.currentStage}/{t.totalStages}</span>
              </div>
              <p className="mt-0.5 truncate text-xs text-slate-400">For: {t.requesterName || "—"}{t.flowType ? ` · ${t.flowType.replace(/_/g, " ")}` : ""}</p>
            </div>
            <ChevronRight size={18} className="mt-1 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-slate-400" />
          </Link>
        ))}
      </div>
    </div>
  );
}

function QuickApps({ role }) {
  const all = NAV.flatMap((g) => g.items).filter((i) => !i.roles || i.roles.includes(role));
  const apps = QUICK_PATHS.map((p) => all.find((i) => i.to === p)).filter(Boolean);
  if (!apps.length) return null;
  return (
    <div className="card p-5" data-testid="quick-apps">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-head text-base font-bold text-slate-900 dark:text-white">Apps</h3>
      </div>
      <div className="grid grid-cols-4 gap-3 sm:grid-cols-6">
        {apps.map((a) => (
          <Link key={a.to} to={a.to} data-testid={`quick-app-${a.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="group flex flex-col items-center gap-2 rounded-xl p-2 text-center transition-transform hover:-translate-y-0.5">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-50 text-accent transition-colors group-hover:bg-accent group-hover:text-white dark:bg-slate-800">
              <a.icon size={22} />
            </span>
            <span className="text-[11px] font-medium leading-tight text-slate-600 dark:text-slate-300">{a.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function RequestsCard() {
  return (
    <div className="card p-5" data-testid="requests-card">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-head text-base font-bold text-slate-900 dark:text-white">Requests</h3>
        <Link to="/approvals" className="text-xs font-semibold text-accent hover:underline">View All</Link>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {REQUESTS.map((r) => (
          <Link key={r.label} to={r.to} data-testid={`request-${r.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={`flex flex-col gap-3 rounded-xl border p-4 shadow-sm transition-transform hover:-translate-y-0.5 ${r.cls}`}>
            <r.icon size={22} />
            <span className="text-sm font-semibold leading-tight">{r.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

const EVENT_META = {
  Leave: { type: "LEAVE", icon: Palmtree, empty: "No one's on leave today" },
  Birthdays: { type: "BIRTHDAY", icon: Cake, empty: "No birthdays this month" },
  Anniversaries: { type: "ANNIVERSARY", icon: PartyPopper, empty: "No work anniversaries this month" },
};

function EventsCard({ events }) {
  const [tab, setTab] = useState("Leave");
  const meta = EVENT_META[tab];
  const list = events.filter((e) => e.type === meta.type);
  return (
    <div className="card overflow-hidden" data-testid="events-card">
      <div className="px-5 pt-5"><h3 className="font-head text-base font-bold text-slate-900 dark:text-white">Events</h3></div>
      <div className="mt-3 flex gap-1 border-b border-slate-100 px-3 dark:border-slate-800">
        {Object.keys(EVENT_META).map((k) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`events-tab-${k.toLowerCase()}`}
            className={`-mb-px border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
              tab === k ? "border-accent text-accent" : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
            }`}>{k}</button>
        ))}
      </div>
      <div className="p-5">
        {list.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-300 dark:bg-slate-800"><meta.icon size={26} /></span>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Team at its strongest</p>
            <p className="text-xs text-slate-400">{meta.empty}</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {list.slice(0, 6).map((e, idx) => (
              <div key={idx} className="flex items-center gap-3" data-testid="event-row">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: e.color }} />
                <span className="flex-1 truncate text-sm text-slate-700 dark:text-slate-200">{e.title}</span>
                <span className="text-xs text-slate-400">{new Date(e.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value, accent }) {
  return (
    <div className="card flex items-center gap-3 p-4" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${accent}`}><Icon size={20} /></span>
      <div>
        <div className="font-head text-xl font-extrabold leading-none text-slate-900 dark:text-white">{value}</div>
        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{label}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const role = user?.role || "EMPLOYEE";
  const [d, setD] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setD(data)).catch(() => setD(false));
    api.get("/announcements").then(({ data }) => setAnnouncements(data.slice(0, 5))).catch(() => {});
    api.get("/approvals/inbox").then(({ data }) => setTasks(data)).catch(() => {});
    api.get("/calendar").then(({ data }) => setEvents(data.events || [])).catch(() => {});
  }, []);

  if (!d) return <Loading />;

  return (
    <div className="animate-fade-up">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-8">
          <Hero announcements={announcements} />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <MiniStat icon={UsersIcon} label="Employees" value={d.stats.totalEmployees} accent="bg-accent/10 text-accent" />
            <MiniStat icon={CheckCircle2} label="Present Today" value={d.stats.presentToday} accent="bg-emerald-500/10 text-emerald-600" />
            <MiniStat icon={Palmtree} label="Pending Leave" value={d.stats.pendingLeave} accent="bg-amber-500/10 text-amber-600" />
            <MiniStat icon={Receipt} label="Expense Claims" value={d.stats.expenseClaims} accent="bg-violet-500/10 text-violet-600" />
          </div>
          <TasksAwaiting tasks={tasks} />
          <QuickApps role={role} />
        </div>

        <div className="space-y-6 lg:col-span-4">
          <RequestsCard />
          <EventsCard events={events} />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="card p-6 lg:col-span-2">
          <h3 className="font-head text-base font-bold text-slate-900 dark:text-white">Department Distribution</h3>
          <div className="mt-4 h-60">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.departmentDistribution}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "rgba(37,99,235,0.06)" }} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]} maxBarSize={56}>
                  {d.departmentDistribution.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card p-6">
          <h3 className="font-head text-base font-bold text-slate-900 dark:text-white">Employee Status</h3>
          <div className="mt-2 h-44">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={d.statusDistribution} dataKey="count" nameKey="name" innerRadius={44} outerRadius={68} paddingAngle={3}>
                  {d.statusDistribution.map((_, i) => <Cell key={i} fill={PIE[i % PIE.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-1.5">
            {d.statusDistribution.map((s, i) => (
              <div key={s.name} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300"><span className="h-2.5 w-2.5 rounded-full" style={{ background: PIE[i % PIE.length] }} />{s.name}</span>
                <span className="font-mono font-semibold text-slate-800 dark:text-slate-100">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
